/**
 * AUTH MODAL WITH ONBOARDING
 * 
 * Modal popup for login/signup
 * After sign-up, shows onboarding step to set username & display name
 * Includes required Terms of Service agreement
 */

import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { signUpWithEmail, signInWithEmail, getCurrentUser, sendVerificationEmail, sendPasswordReset } from '../firebase/auth';
import { User, AtSign, Loader2, CheckCircle, XCircle, Eye, EyeOff, Mail, Key, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AppIcons } from '../constants/icons';

interface AuthModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

type ModalStep = 'auth' | 'onboarding' | 'forgotPassword' | 'verifyEmail';

const AuthModal: React.FC<AuthModalProps> = ({ onClose, onSuccess }) => {
  // Auth state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Modal step state
  const [step, setStep] = useState<ModalStep>('auth');
  
  // Onboarding state
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Forgot password state
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);

  // Resend verification state
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [verificationResent, setVerificationResent] = useState(false);

  // Handle auth form submission
  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');

    // Validate password match for sign up
    if (isSignUp && password !== confirmPassword) {
      setMessage('❌ Passwords do not match');
      return;
    }

    // Validate password length
    if (password.length < 8) {
      setMessage('❌ Password must be at least 8 characters');
      return;
    }

    // Validate terms agreement for sign up
    if (isSignUp && !agreedToTerms) {
      setMessage('❌ Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    setIsLoading(true);

    try {
      if (isSignUp) {
        console.log('[AuthModal] Starting signup for:', email);
        await signUpWithEmail(email, password);
        console.log('[AuthModal] Signup successful, sending verification email...');

        // Send verification email
        try {
          await sendVerificationEmail();
          console.log('[AuthModal] Verification email sent successfully');
          setMessage('✅ Account created! Check your email to verify.');
        } catch (verifyError: any) {
          console.error('[AuthModal] Error sending verification email:', verifyError?.code, verifyError?.message, verifyError);
          // Continue anyway - they can request another later
          setMessage('✅ Account created!');
        }
        
        // Move to onboarding step
        setTimeout(() => {
          setStep('onboarding');
          setMessage('');
        }, 500);
        
      } else {
        await signInWithEmail(email, password);
        setMessage('✅ Logged in!');
        
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 500);
      }
      
    } catch (error: any) {
      setMessage(`❌ ${error.message}`);
      setIsLoading(false);
    }
  };

  // Username validation
  const validateUsername = (value: string): string => {
    if (value.length < 3) {
      return 'Username must be at least 3 characters';
    }
    if (value.length > 30) {
      return 'Username must be 30 characters or less';
    }
    if (!/^[a-zA-Z0-9_]+$/.test(value)) {
      return 'Only letters, numbers, and underscores allowed';
    }
    return '';
  };

  // Check username availability
  useEffect(() => {
    if (step !== 'onboarding' || !username) {
      setUsernameAvailable(null);
      setUsernameError('');
      return;
    }

    const checkUsername = async () => {
      const error = validateUsername(username);
      if (error) {
        setUsernameError(error);
        setUsernameAvailable(null);
        return;
      }

      setUsernameChecking(true);
      setUsernameError('');

      try {
        const usernameDocRef = doc(db, 'usernames', username.toLowerCase());
        const usernameDoc = await getDoc(usernameDocRef);

        if (usernameDoc.exists()) {
          setUsernameAvailable(false);
          setUsernameError('Username is already taken');
        } else {
          setUsernameAvailable(true);
        }
      } catch (error) {
        console.error('Error checking username:', error);
      } finally {
        setUsernameChecking(false);
      }
    };

    const debounce = setTimeout(checkUsername, 500);
    return () => clearTimeout(debounce);
  }, [username, step]);

  // Handle onboarding form submission
  const handleOnboardingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const user = getCurrentUser();
    if (!user) {
      setMessage('❌ Not logged in. Please try again.');
      return;
    }

    if (!usernameAvailable) {
      setMessage('❌ Please choose an available username.');
      return;
    }

    setIsSavingProfile(true);
    setMessage('');

    try {
      // Reserve the username
      const usernameDocRef = doc(db, 'usernames', username.toLowerCase());
      await setDoc(usernameDocRef, {
        uid: user.uid,
        reservedAt: Timestamp.now()
      });

      // Create/update user profile
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        username,
        displayName: displayName.trim() || username,
        email: user.email,
        createdAt: Timestamp.now(),
        usernameLastChanged: Timestamp.now(),
        agreedToTermsAt: Timestamp.now(),
        profile: {
          username,
          usernameLower: username.toLowerCase(),
          displayName: displayName.trim() || username,
          bio: '',
          avatarUrl: '',
          bannerUrl: '',
          socialLinks: {}
        },
        privacy: {
          isPublic: false,
          showCollection: false,
          showWishlist: false,
          showDestashList: false,
          showFavorites: false,
          showDestashArchive: false,
          showProjects: false,
          showStats: false,
          allowMessages: true
        },
        preferences: {
          theme: 'light',
          defaultItemsPerPage: 20,
          emailNotifications: {
            destashInquiry: true,
            weeklyDigest: false
          }
        }
      }, { merge: true });

      setMessage('✅ Profile created!');
      
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 500);
      
    } catch (error: any) {
      console.error('Error saving profile:', error);
      setMessage(`❌ Failed to save profile: ${error.message}`);
      setIsSavingProfile(false);
    }
  };

  // Skip onboarding (will need to set username in settings later)
  // Keyboard shortcut (only for auth step)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && step === 'auth') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [step, onClose]);

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={step === 'auth' ? onClose : undefined}
    >
      <div 
        className="glass-modal max-w-md w-full p-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* AUTH STEP */}
        {step === 'auth' && (
          <>
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-glass font-heading flex items-center gap-2">
                {isSignUp ? (
                  <>
                    <AppIcons.Join className="w-6 h-6" />
                    Join the Collection
                  </>
                ) : (
                  <>
                    <AppIcons.Welcome className="w-6 h-6" />
                    Welcome Back
                  </>
                )}
              </h2>
              <button
                onClick={onClose}
                className="text-glass-muted hover:text-glass-secondary text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleAuthSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-glass-secondary mb-2 font-body">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="input-glass rounded-lg font-body"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-glass-secondary mb-2 font-body">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="input-glass rounded-lg pr-12 font-body"
                    placeholder={isSignUp ? 'At least 8 characters' : '••••••••'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-glass-muted hover:text-glass-secondary transition-default"
                  >
                    {showPassword ? <Eye className="icon-md" /> : <EyeOff className="icon-md" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password - Only show for sign up */}
              {isSignUp && (
                <div>
                  <label className="block text-sm font-bold text-glass-secondary mb-2 font-body">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      className={`input-glass rounded-lg pr-12 ${
                        confirmPassword && password !== confirmPassword
                          ? 'border-red-400 focus:ring-red-500'
                          : confirmPassword && password === confirmPassword
                            ? 'border-green-400 focus:ring-green-500'
                            : ''
                      }`}
                      placeholder="Re-enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-glass-muted hover:text-glass-secondary transition-default"
                    >
                      {showConfirmPassword ? <Eye className="icon-md" /> : <EyeOff className="icon-md" />}
                    </button>
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
                  )}
                  {confirmPassword && password === confirmPassword && (
                    <p className="text-green-400 text-sm mt-1">✓ Passwords match</p>
                  )}
                </div>
              )}

              {/* Terms of Service Agreement - Only for sign up */}
              {isSignUp && (
                <div className="flex items-start gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="terms-agreement-modal"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-pink-500 focus:ring-pink-500 cursor-pointer"
                  />
                  <label 
                    htmlFor="terms-agreement-modal" 
                    className="text-sm text-glass-secondary font-body cursor-pointer"
                  >
                    I agree to the{' '}
                    <Link 
                      to="/terms" 
                      target="_blank"
                      className="text-glass hover:underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Terms of Service
                    </Link>
                    ,{' '}
                    <Link 
                      to="/privacy" 
                      target="_blank"
                      className="text-glass hover:underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Privacy Policy
                    </Link>
                    ,{' '}
                    <Link 
                      to="/refund-policy" 
                      target="_blank"
                      className="text-glass hover:underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Refund Policy
                    </Link>
                    , and{' '}
                    <Link 
                      to="/copyright" 
                      target="_blank"
                      className="text-glass hover:underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Copyright Policy
                    </Link>
                  </label>
                </div>
              )}

              {message && (
                <div className={`font-body ${message.startsWith('✅') ? 'alert-success' : 'alert-error'}`}>
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || (isSignUp && password !== confirmPassword) || (isSignUp && !confirmPassword) || (isSignUp && !agreedToTerms)}
                className="btn-primary w-full rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-body"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {isSignUp ? 'Creating account...' : 'Logging in...'}
                  </>
                ) : isSignUp ? (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Sign Up
                  </>
                ) : (
                  <>
                    <Key className="w-5 h-5" />
                    Log In
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center space-y-3">
              {/* Forgot Password - only show on login */}
              {!isSignUp && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setStep('forgotPassword');
                    setForgotPasswordEmail(email); // Pre-fill with email if entered
                    setMessage('');
                    setForgotPasswordSent(false);
                  }}
                  className="text-sm text-glass-muted hover:text-glass-secondary font-medium font-body"
                >
                  Forgot your password?
                </button>
              )}
              
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsSignUp(!isSignUp);
                  setConfirmPassword('');
                  setMessage('');
                  setAgreedToTerms(false);
                }}
                className="text-sm text-glass-secondary hover:text-glass font-medium block w-full font-body"
              >
                {isSignUp ? 'Already have an account? Log in' : "Don't have an account? Sign up"}
              </button>
            </div>
            
            <p className="mt-4 text-xs text-center text-glass-muted font-body">
              Press ESC to close
            </p>
          </>
        )}

        {/* ONBOARDING STEP */}
        {step === 'onboarding' && (
          <>
            {/* Header */}
            <div className="text-center mb-6">
              <div className="text-5xl mb-3">💅</div>
              <h2 className="text-2xl font-bold text-glass font-heading">
                Set Up Your Profile
              </h2>
              <p className="text-glass-muted mt-2 font-body">
                Choose a username for your public collection URL
              </p>
            </div>

            {/* Email verification reminder */}
            <div className="bg-purple-500/20 border border-purple-400/30 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <Mail className="w-5 h-5 text-purple-300 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-purple-200 font-body">
                    We sent a verification email to your inbox. Please check and verify when you get a chance!
                  </p>
                  {verificationResent ? (
                    <p className="text-sm text-green-400 mt-2 font-body">
                      ✓ Verification email resent! Check your inbox (and spam folder).
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={async () => {
                        setIsResendingVerification(true);
                        try {
                          await sendVerificationEmail();
                          setVerificationResent(true);
                        } catch (error: any) {
                          console.error('Error resending verification:', error);
                          alert(error.message || 'Failed to resend. Please try again later.');
                        } finally {
                          setIsResendingVerification(false);
                        }
                      }}
                      disabled={isResendingVerification}
                      className="text-sm text-purple-300 hover:text-white underline mt-2 font-body disabled:opacity-50"
                    >
                      {isResendingVerification ? 'Sending...' : "Didn't receive it? Resend verification email"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <form onSubmit={handleOnboardingSubmit} className="space-y-4">
              {/* Username */}
              <div>
                <label className="block text-sm font-bold text-glass-secondary mb-2 font-body">
                  <AtSign className="icon-sm inline mr-1" />
                  Username <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                    required
                    maxLength={30}
                    className={`input-glass rounded-lg pr-10 ${
                      usernameError
                        ? 'border-red-400 focus:ring-red-500'
                        : usernameAvailable
                          ? 'border-green-400 focus:ring-green-500'
                          : ''
                    }`}
                    placeholder="your_username"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {usernameChecking ? (
                      <Loader2 className="w-5 h-5 text-glass-muted animate-spin" />
                    ) : usernameError ? (
                      <XCircle className="icon-md text-red-500" />
                    ) : usernameAvailable ? (
                      <CheckCircle className="icon-md text-green-500" />
                    ) : null}
                  </div>
                </div>
                {usernameError && (
                  <p className="text-red-500 text-sm mt-1">{usernameError}</p>
                )}
                {usernameAvailable && !usernameError && (
                  <p className="text-green-400 text-sm mt-1">✓ Username available!</p>
                )}
                <p className="text-glass-muted text-xs mt-1">
                  Your profile: polishallthethings.com/profile/{username || 'username'}
                </p>
              </div>

              {/* Display Name */}
              <div>
                <label className="block text-sm font-bold text-glass-secondary mb-2 font-body">
                  <User className="icon-sm inline mr-1" />
                  Display Name <span className="text-glass-muted font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={50}
                  className="input-glass rounded-lg font-body"
                  placeholder="How you want to be called"
                />
                <p className="text-glass-muted text-xs mt-1">
                  This is what others will see. You can change it anytime.
                </p>
              </div>

              {message && (
                <div className={`font-body ${message.startsWith('✅') ? 'alert-success' : 'alert-error'}`}>
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={isSavingProfile || !usernameAvailable || usernameError !== ''}
                className="btn-primary w-full rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 font-body"
              >
                {isSavingProfile ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating profile...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Complete Setup
                  </>
                )}
              </button>
            </form>
          </>
        )}

        {/* FORGOT PASSWORD STEP */}
        {step === 'forgotPassword' && (
          <>
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-glass font-heading flex items-center gap-2">
                <Key className="w-6 h-6" />
                Reset Password
              </h2>
              <button
                onClick={onClose}
                className="text-glass-muted hover:text-glass-secondary text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {!forgotPasswordSent ? (
              <>
                <p className="text-glass-secondary mb-6 font-body">
                  Enter your email address and we'll send you a link to reset your password.
                </p>

                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setIsSendingReset(true);
                  setMessage('');
                  
                  try {
                    await sendPasswordReset(forgotPasswordEmail);
                    setForgotPasswordSent(true);
                  } catch (error: any) {
                    setMessage(`❌ ${error.message}`);
                  } finally {
                    setIsSendingReset(false);
                  }
                }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-glass-secondary mb-2 font-body">
                      Email
                    </label>
                    <input
                      type="email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      required
                      className="input-glass rounded-lg font-body"
                      placeholder="your@email.com"
                    />
                  </div>

                  {message && (
                    <div className="alert-error font-body">{message}</div>
                  )}

                  <button
                    type="submit"
                    disabled={isSendingReset || !forgotPasswordEmail}
                    className="btn-primary w-full rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 font-body"
                  >
                    {isSendingReset ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5" />
                        Send Reset Link
                      </>
                    )}
                  </button>
                </form>
              </>
            ) : (
              <>
                <div className="text-center py-6">
                  <div className="mb-4 flex justify-center">
                    <Mail className="w-16 h-16 text-glass" />
                  </div>
                  <h3 className="text-xl font-bold text-glass mb-2 font-body">
                    Check your inbox!
                  </h3>
                  <p className="text-glass-secondary mb-2 font-body">
                    We've sent a password reset link to:
                  </p>
                  <p className="font-bold text-glass-secondary mb-4 font-body">
                    {forgotPasswordEmail}
                  </p>
                  <p className="text-glass-muted text-sm mb-6 font-body flex items-center justify-center gap-1">
                    <Mail className="w-4 h-4" />
                    <strong>Tip:</strong> Check your spam/junk folder if you don't see it!
                  </p>
                </div>

                <button
                  onClick={() => {
                    setStep('auth');
                    setForgotPasswordSent(false);
                    setMessage('');
                  }}
                  className="btn-primary w-full rounded-lg font-body"
                >
                  ← Back to Login
                </button>
              </>
            )}

            {!forgotPasswordSent && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => {
                    setStep('auth');
                    setMessage('');
                  }}
                  className="text-sm text-glass-muted hover:text-glass-secondary font-medium font-body"
                >
                  ← Back to Login
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
